import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import {
  Package, LayoutGrid, Box, CalendarCheck, Clock, XCircle, AlertTriangle,
  RefreshCcw, Hourglass, Users, Calendar, Euro, Table2, Eye, Pencil,
  Zap, Plus, X, Check, Trash2, Settings, Menu,
} from 'lucide-react';
import { useSidebar } from 'contexts/SidebarContext';
import {
  getSubscriptionsSummary,
  getAllSubscriptions,
  getSubscriptionById,
  updateSubscription,
  getUsersBySubscription,
  getAnnualUsers,
  getPaymentPendingUsers,
  getExpiredUsers,
  bulkUpdateSubscriptions,
  getAllActions,
  getSubscriptionActions,
  updateActionLimit,
  addActionToSubscription,
  removeActionFromSubscription,
  SubscriptionsSummaryResponse,
  SubscriptionDetail,
  SubscriptionUser,
  UpdateSubscriptionPayload,
  BulkUpdatePayload,
  Action,
  ActionWithLimit,
} from 'services/admin/subscriptionAdminService';

type TabType = 'overview' | 'subscriptions' | 'annual' | 'pending' | 'expired';

const SubscriptionManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Summary data
  const [summary, setSummary] = useState<SubscriptionsSummaryResponse['data'] | null>(null);

  // Subscriptions list
  const [subscriptions, setSubscriptions] = useState<SubscriptionDetail[]>([]);

  // Selected subscription for viewing users
  const [selectedSubscription, setSelectedSubscription] = useState<string | null>(null);
  const [subscriptionUsers, setSubscriptionUsers] = useState<SubscriptionUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPagination, setUsersPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [billingCycleFilter, setBillingCycleFilter] = useState<'all' | 'monthly' | 'annual'>('all');

  // Annual users
  const [annualUsers, setAnnualUsers] = useState<SubscriptionUser[]>([]);
  const [annualStats, setAnnualStats] = useState<any>(null);
  const [annualPagination, setAnnualPagination] = useState({ page: 1, total: 0, totalPages: 0 });

  // Pending payments
  const [pendingUsers, setPendingUsers] = useState<SubscriptionUser[]>([]);
  const [pendingStats, setPendingStats] = useState<any>(null);
  const [pendingPagination, setPendingPagination] = useState({ page: 1, total: 0, totalPages: 0 });

  // Expired users
  const [expiredUsers, setExpiredUsers] = useState<SubscriptionUser[]>([]);
  const [expiredPagination, setExpiredPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [expiredDaysAgo, setExpiredDaysAgo] = useState(30);

  // Edit modal
  const [editModal, setEditModal] = useState<{ open: boolean; subscription: SubscriptionDetail | null }>({
    open: false,
    subscription: null,
  });
  const [editForm, setEditForm] = useState<UpdateSubscriptionPayload>({});
  const [saving, setSaving] = useState(false);

  // Bulk actions
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkActionModal, setBulkActionModal] = useState(false);
  const [bulkAction, setBulkAction] = useState<'extend' | 'suspend' | 'cancel'>('extend');
  const [extendDays, setExtendDays] = useState(28);

  // Action management
  const [actionsModal, setActionsModal] = useState<{ open: boolean; subscription: SubscriptionDetail | null }>({
    open: false,
    subscription: null,
  });
  const [allActions, setAllActions] = useState<Action[]>([]);
  const [subscriptionActions, setSubscriptionActions] = useState<ActionWithLimit[]>([]);
  const [availableActions, setAvailableActions] = useState<Action[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [editingAction, setEditingAction] = useState<{ actionId: string; limit: number; limitUnit: 'day' | 'week' } | null>(null);
  const [addActionForm, setAddActionForm] = useState<{ actionId: string; limit: number; limitUnit: 'day' | 'week' }>({ actionId: '', limit: 100, limitUnit: 'day' });
  const [showAddAction, setShowAddAction] = useState(false);
  const [actionSaving, setActionSaving] = useState(false);

  // Store actions count per subscription
  const [subscriptionActionsMap, setSubscriptionActionsMap] = useState<Record<string, number>>({});

  // Theme tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const cardBg = isDark ? '#1D293D' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const iconContainerBg = isDark ? '#3C0366' : '#FAF5FF';
  const iconColor = isDark ? '#C27AFF' : '#9333EA';
  const buttonBg = mainBg;
  const buttonBorder = isDark ? '#314158' : '#E2E8F0';
  const activeTabBorder = isDark ? '#2B7FFF' : '#2563EB';
  const activeTabText = isDark ? '#51A2FF' : '#2563EB';
  const inactiveTabText = isDark ? '#90A1B9' : '#62748E';
  const tableBg = isDark ? '#0F172B' : '#FFFFFF';
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableRowHoverBg = isDark ? '#162456' : '#F8FAFC';
  const tableBorderColor = isDark ? '#1D293D' : '#F1F5F9';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#374151';
  const modalBg = isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)';
  const dangerBg = isDark ? '#461901' : '#FEF2F2';
  const dangerBorder = isDark ? '#EC003F' : '#FECACA';
  const dangerText = isDark ? '#FF637E' : '#DC2626';

  // Additional derived tokens
  const accentBlue = isDark ? '#51A2FF' : '#2563EB';
  const accentGreen = isDark ? '#34D399' : '#059669';
  const accentPurple = isDark ? '#C27AFF' : '#9333EA';
  const accentOrange = isDark ? '#FB923C' : '#EA580C';
  const badgeBlueBg = isDark ? '#162456' : '#EFF6FF';
  const badgeBlueText = isDark ? '#51A2FF' : '#2563EB';
  const badgeGreenBg = isDark ? '#052E16' : '#F0FDF4';
  const badgeGreenText = isDark ? '#34D399' : '#15803D';
  const badgePurpleBg = isDark ? '#3C0366' : '#F5F3FF';
  const badgePurpleText = isDark ? '#C27AFF' : '#7C3AED';
  const badgeYellowBg = isDark ? '#422006' : '#FEFCE8';
  const badgeYellowText = isDark ? '#FBBF24' : '#A16207';
  const badgeGrayBg = isDark ? '#1D293D' : '#F3F4F6';
  const badgeGrayText = isDark ? '#90A1B9' : '#4B5563';
  const codeBg = isDark ? '#162456' : '#F3F4F6';
  const codeText = isDark ? '#90A1B9' : '#6B7280';
  const stripeBg = isDark ? '#1D293D' : '#F8FAFC';

  useEffect(() => {
    fetchSummary();
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    if (activeTab === 'annual') {
      fetchAnnualUsers(1);
    } else if (activeTab === 'pending') {
      fetchPendingUsers(1);
    } else if (activeTab === 'expired') {
      fetchExpiredUsers(1);
    }
  }, [activeTab]);

  const fetchSummary = async () => {
    try {
      const response = await getSubscriptionsSummary();
      setSummary(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch summary');
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllSubscriptions();
      const subs = response.data || [];
      setSubscriptions(subs);

      // Fetch actions counts for all subscriptions
      if (subs.length > 0) {
        fetchAllSubscriptionActionsCounts(subs);
      }
    } catch (error: any) {
      console.error('Failed to fetch subscriptions:', error);
      setError(error.message || 'Failed to fetch subscriptions');
      toast.error(error.message || 'Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionUsers = async (subscriptionName: string, page: number = 1) => {
    try {
      setUsersLoading(true);
      const response = await getUsersBySubscription(subscriptionName, {
        page,
        limit: 10,
        billingCycle: billingCycleFilter === 'all' ? undefined : billingCycleFilter,
      });
      setSubscriptionUsers(response.data.users);
      setUsersPagination({
        page: response.data.pagination.page,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchAnnualUsers = async (page: number = 1) => {
    try {
      setUsersLoading(true);
      const response = await getAnnualUsers({ page, limit: 10 });
      setAnnualUsers(response.data.users);
      setAnnualStats(response.data.stats);
      setAnnualPagination({
        page: response.data.pagination.page,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch annual users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchPendingUsers = async (page: number = 1) => {
    try {
      setUsersLoading(true);
      const response = await getPaymentPendingUsers({ page, limit: 10 });
      setPendingUsers(response.data.users);
      setPendingStats(response.data.stats);
      setPendingPagination({
        page: response.data.pagination.page,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch pending users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchExpiredUsers = async (page: number = 1) => {
    try {
      setUsersLoading(true);
      const response = await getExpiredUsers({ page, limit: 10, daysAgo: expiredDaysAgo });
      setExpiredUsers(response.data.users);
      setExpiredPagination({
        page: response.data.pagination.page,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch expired users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleEditSubscription = async (subscriptionId: string) => {
    try {
      const response = await getSubscriptionById(subscriptionId);
      setEditModal({ open: true, subscription: response.data });
      const desc = response.data.description;
      const tag = response.data.tagline;
      setEditForm({
        description: typeof desc === 'string' ? { en: desc, fr: desc } : (desc || { en: '', fr: '' }),
        tagline: typeof tag === 'string' ? { en: tag, fr: tag } : (tag || { en: '', fr: '' }),
        number_of_accounts: response.data.number_of_accounts,
        price: {
          amount_monthly: response.data.price.amount_monthly,
          amount_annually: response.data.price.amount_annually,
          amount_4weeks: response.data.price.amount_4weeks,
          amount_4weeks_annual: response.data.price.amount_4weeks_annual,
          discount_percentage: response.data.price.discount_percentage,
        },
        stripe_price_ids: {
          monthly: response.data.stripe_price_ids?.monthly || '',
          annual: response.data.stripe_price_ids?.annual || '',
        },
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch subscription details');
    }
  };

  const handleSaveSubscription = async () => {
    if (!editModal.subscription) return;

    try {
      setSaving(true);
      await updateSubscription(editModal.subscription._id, editForm);
      toast.success('Subscription updated successfully');
      setEditModal({ open: false, subscription: null });
      fetchSubscriptions();
      fetchSummary();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleViewUsers = (subscriptionName: string) => {
    setSelectedSubscription(subscriptionName);
    fetchSubscriptionUsers(subscriptionName, 1);
  };

  const handleBulkUpdate = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('Please select users first');
      return;
    }

    try {
      const payload: BulkUpdatePayload = {
        userIds: selectedUserIds,
        action: bulkAction,
      };

      if (bulkAction === 'extend') {
        payload.days = extendDays;
      }

      const response = await bulkUpdateSubscriptions(payload);
      toast.success(response.message);
      setBulkActionModal(false);
      setSelectedUserIds([]);

      // Refresh current view
      if (selectedSubscription) {
        fetchSubscriptionUsers(selectedSubscription, usersPagination.page);
      } else if (activeTab === 'annual') {
        fetchAnnualUsers(annualPagination.page);
      } else if (activeTab === 'pending') {
        fetchPendingUsers(pendingPagination.page);
      } else if (activeTab === 'expired') {
        fetchExpiredUsers(expiredPagination.page);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update users');
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = (users: SubscriptionUser[]) => {
    const allIds = users.map(u => u.user_id);
    const allSelected = allIds.every(id => selectedUserIds.includes(id));

    if (allSelected) {
      setSelectedUserIds(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedUserIds(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  // Action Management Functions
  const fetchAllActions = async () => {
    try {
      const response = await getAllActions();
      setAllActions(response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch actions:', error);
    }
  };

  const fetchSubscriptionActionsData = async (subscriptionId: string, updateMap: boolean = false) => {
    try {
      setActionsLoading(true);
      const response = await getSubscriptionActions(subscriptionId);
      const actions = response.data.actions || [];
      const available = response.data.availableActions || [];

      setSubscriptionActions(actions);
      setAvailableActions(available);

      // Update the actions count map if needed
      if (updateMap) {
        setSubscriptionActionsMap(prev => ({
          ...prev,
          [subscriptionId]: actions.length
        }));
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch subscription actions');
    } finally {
      setActionsLoading(false);
    }
  };

  // Fetch actions count for all subscriptions when subscriptions are loaded
  const fetchAllSubscriptionActionsCounts = async (subs: SubscriptionDetail[]) => {
    const countsMap: Record<string, number> = {};

    await Promise.all(
      subs.map(async (sub) => {
        try {
          const response = await getSubscriptionActions(sub._id);
          countsMap[sub._id] = response.data.actions?.length || 0;
        } catch {
          countsMap[sub._id] = 0;
        }
      })
    );

    setSubscriptionActionsMap(countsMap);
  };

  const handleOpenActionsModal = (subscription: SubscriptionDetail) => {
    setActionsModal({ open: true, subscription });
    fetchSubscriptionActionsData(subscription._id);
  };

  const handleCloseActionsModal = () => {
    // Update the count for this subscription before closing
    if (actionsModal.subscription) {
      setSubscriptionActionsMap(prev => ({
        ...prev,
        [actionsModal.subscription!._id]: subscriptionActions.length
      }));
    }
    setActionsModal({ open: false, subscription: null });
    setSubscriptionActions([]);
    setAvailableActions([]);
    setEditingAction(null);
    setShowAddAction(false);
    setAddActionForm({ actionId: '', limit: 100, limitUnit: 'day' });
  };

  const handleUpdateActionLimit = async () => {
    if (!actionsModal.subscription || !editingAction) return;

    try {
      setActionSaving(true);
      await updateActionLimit(actionsModal.subscription._id, editingAction.actionId, {
        action_limit: editingAction.limit,
        limit_unit: editingAction.limitUnit,
      });
      toast.success(t('adminSubscriptions.actions.limitUpdated') || 'Action limit updated');
      setEditingAction(null);
      fetchSubscriptionActionsData(actionsModal.subscription._id);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update action limit');
    } finally {
      setActionSaving(false);
    }
  };

  const handleAddAction = async () => {
    if (!actionsModal.subscription || !addActionForm.actionId) {
      toast.error('Please select an action');
      return;
    }

    try {
      setActionSaving(true);
      await addActionToSubscription(actionsModal.subscription._id, {
        action_id: addActionForm.actionId,
        action_limit: addActionForm.limit,
        limit_unit: addActionForm.limitUnit,
      });
      toast.success(t('adminSubscriptions.actions.added') || 'Action added to subscription');
      setShowAddAction(false);
      setAddActionForm({ actionId: '', limit: 100, limitUnit: 'day' });
      fetchSubscriptionActionsData(actionsModal.subscription._id);
    } catch (error: any) {
      toast.error(error.message || 'Failed to add action');
    } finally {
      setActionSaving(false);
    }
  };

  const handleRemoveAction = async (actionId: string) => {
    if (!actionsModal.subscription) return;

    if (!window.confirm(t('adminSubscriptions.actions.confirmRemove') || 'Are you sure you want to remove this action?')) {
      return;
    }

    try {
      setActionSaving(true);
      await removeActionFromSubscription(actionsModal.subscription._id, actionId);
      toast.success(t('adminSubscriptions.actions.removed') || 'Action removed from subscription');
      fetchSubscriptionActionsData(actionsModal.subscription._id);
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove action');
    } finally {
      setActionSaving(false);
    }
  };

  const getActionName = (actionId: string): string => {
    const action = allActions.find(a => a._id === actionId);
    return action?.friendly_name || action?.name || actionId;
  };

  // Use availableActions from API response (actions not yet assigned to this subscription)
  const getAvailableActionsForAdd = (): Action[] => {
    return availableActions;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('adminSubscriptions.tabs.overview') || 'Overview', icon: <LayoutGrid size={14} /> },
    { id: 'subscriptions', label: t('adminSubscriptions.tabs.subscriptions') || 'Subscriptions', icon: <Box size={14} /> },
    { id: 'annual', label: t('adminSubscriptions.tabs.annual') || 'Annual Users', icon: <CalendarCheck size={14} /> },
    { id: 'pending', label: t('adminSubscriptions.tabs.pending') || 'Pending Payments', icon: <Clock size={14} /> },
    { id: 'expired', label: t('adminSubscriptions.tabs.expired') || 'Expired', icon: <XCircle size={14} /> },
  ];

  if (loading) {
    return <Spinner size="large" text={t('common.loading')} fullPage />;
  }

  if (error && !summary && subscriptions.length === 0) {
    return (
      <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ gap: '8px', background: containerBg }}>
        <div className="flex flex-col items-center justify-center flex-1 w-full rounded-none lg:rounded-2xl"
          style={{ background: mainBg, boxShadow: mainShadow, padding: '32px', textAlign: 'center' }}>
          <div
            className="flex items-center justify-center mx-auto"
            style={{
              width: 64, height: 64, borderRadius: '50%',
              background: dangerBg, marginBottom: 16,
            }}
          >
            <AlertTriangle size={24} style={{ color: dangerText }} />
          </div>
          <h2 className="text-lg font-semibold" style={{ color: titleColor, marginBottom: 8 }}>
            {t('adminSubscriptions.error') || 'Failed to Load Data'}
          </h2>
          <p className="text-sm" style={{ color: subtitleColor, marginBottom: 16 }}>{error}</p>
          <button
            onClick={() => {
              fetchSummary();
              fetchSubscriptions();
            }}
            className="px-4 py-2 text-sm font-medium rounded-md"
            style={{ background: accentBlue, color: '#FFFFFF' }}
          >
            <RefreshCcw size={14} className="inline mr-2" />
            {t('common.retry') || 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start flex-1 min-h-0 overflow-hidden font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ gap: '8px', background: containerBg }}>
      <div className="flex flex-col items-start flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{ background: mainBg, boxShadow: mainShadow }}>

        {/* Header */}
        <div className="flex flex-col justify-center items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl"
          style={{ padding: '12px 16px', gap: '8px', borderBottom: `1px solid ${headerBorder}` }}>
          <div className="flex flex-row items-center w-full" style={{ gap: '8px' }}>
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', background: iconContainerBg, borderRadius: '12px', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Package size={20} style={{ color: iconColor }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ fontFamily: 'Geist, sans-serif', fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0 }}>
                {t('adminSubscriptions.title') || 'Subscription Management'}
              </h1>
            </div>
          </div>
          {/* Tab Navigation */}
          <div className="w-full overflow-x-auto">
            <div style={{ display: 'flex', border: `1px solid ${headerBorder}`, borderRadius: '8px', overflow: 'hidden', height: '36px', width: 'fit-content' }}>
              {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setSelectedSubscription(null);
                      setSelectedUserIds([]);
                    }}
                    style={{
                      padding: '0 12px',
                      fontSize: '14px',
                      fontWeight: 500,
                      border: 'none',
                      cursor: 'pointer',
                      background: isActive ? (isDark ? '#1C398E' : '#EFF6FF') : 'transparent',
                      color: isActive ? (isDark ? '#51A2FF' : '#2B7FFF') : subtitleColor,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Scrollable content area */}
        <div
          className={`flex-1 min-h-0 overflow-y-auto w-full ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
          style={{ padding: '16px' }}
        >
            {/* Overview Tab */}
            {activeTab === 'overview' && !summary && (
              <div
                className="text-center"
                style={{
                  borderRadius: 12, background: cardBg,
                  border: `1px solid ${cardBorder}`, padding: 32,
                }}
              >
                <div
                  className="flex items-center justify-center mx-auto"
                  style={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: isDark ? '#1D293D' : '#F3F4F6', marginBottom: 12,
                  }}
                >
                  <Hourglass size={20} style={{ color: subtitleColor }} />
                </div>
                <p className="text-sm" style={{ color: subtitleColor }}>
                  {t('adminSubscriptions.loadingSummary') || 'Loading summary data...'}
                </p>
                <button
                  onClick={fetchSummary}
                  className="mt-3 px-4 py-2 text-xs font-medium rounded-md"
                  style={{
                    color: accentBlue,
                    border: `1px solid ${isDark ? '#314158' : '#BFDBFE'}`,
                    background: buttonBg,
                  }}
                >
                  <RefreshCcw size={12} className="inline mr-1" />
                  Refresh
                </button>
              </div>
            )}
            {activeTab === 'overview' && summary && (
              <div className="space-y-5">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div
                    className="rounded-lg"
                    style={{ background: cardBg, border: `1px solid ${cardBorder}`, padding: 20 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs" style={{ color: subtitleColor }}>
                          {t('adminSubscriptions.stats.totalActive') || 'Total Active Users'}
                        </p>
                        <p className="text-2xl font-bold mt-1" style={{ color: titleColor }}>
                          {summary.totals.activeUsers}
                        </p>
                      </div>
                      <div
                        className="flex items-center justify-center rounded-lg"
                        style={{
                          width: 40, height: 40,
                          background: badgeBlueBg,
                        }}
                      >
                        <Users size={18} style={{ color: accentBlue }} />
                      </div>
                    </div>
                  </div>

                  <div
                    className="rounded-lg"
                    style={{ background: cardBg, border: `1px solid ${cardBorder}`, padding: 20 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs" style={{ color: subtitleColor }}>
                          {t('adminSubscriptions.stats.monthlyUsers') || 'Monthly Users'}
                        </p>
                        <p className="text-2xl font-bold mt-1" style={{ color: titleColor }}>
                          {summary.totals.monthlyUsers}
                        </p>
                      </div>
                      <div
                        className="flex items-center justify-center rounded-lg"
                        style={{
                          width: 40, height: 40,
                          background: badgeGreenBg,
                        }}
                      >
                        <Calendar size={18} style={{ color: accentGreen }} />
                      </div>
                    </div>
                  </div>

                  <div
                    className="rounded-lg"
                    style={{ background: cardBg, border: `1px solid ${cardBorder}`, padding: 20 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs" style={{ color: subtitleColor }}>
                          {t('adminSubscriptions.stats.annualUsers') || 'Annual Users'}
                        </p>
                        <p className="text-2xl font-bold mt-1" style={{ color: titleColor }}>
                          {summary.totals.annualUsers}
                        </p>
                      </div>
                      <div
                        className="flex items-center justify-center rounded-lg"
                        style={{
                          width: 40, height: 40,
                          background: badgePurpleBg,
                        }}
                      >
                        <CalendarCheck size={18} style={{ color: accentPurple }} />
                      </div>
                    </div>
                  </div>

                  <div
                    className="rounded-lg"
                    style={{ background: cardBg, border: `1px solid ${cardBorder}`, padding: 20 }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs" style={{ color: subtitleColor }}>
                          {t('adminSubscriptions.stats.pendingPayments') || 'Pending Payments'}
                        </p>
                        <p className="text-2xl font-bold mt-1" style={{ color: accentOrange }}>
                          {summary.totals.pendingPayments}
                        </p>
                      </div>
                      <div
                        className="flex items-center justify-center rounded-lg"
                        style={{
                          width: 40, height: 40,
                          background: isDark ? '#422006' : '#FFF7ED',
                        }}
                      >
                        <Clock size={18} style={{ color: accentOrange }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Revenue Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {summary.revenue.map(rev => (
                    <div
                      key={rev._id}
                      className="rounded-lg"
                      style={{ background: cardBg, border: `1px solid ${cardBorder}`, padding: 20 }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs capitalize" style={{ color: subtitleColor }}>{rev._id} Revenue</p>
                          <p className="text-2xl font-bold mt-1" style={{ color: accentGreen }}>
                            {formatCurrency(rev.totalRevenue)}
                          </p>
                          <p className="text-xs mt-1" style={{ color: subtitleColor }}>{rev.count} users</p>
                        </div>
                        <div
                          className="flex items-center justify-center rounded-lg"
                          style={{
                            width: 40, height: 40,
                            background: badgeGreenBg,
                          }}
                        >
                          <Euro size={18} style={{ color: accentGreen }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Subscriptions Summary Table */}
                <div
                  className="rounded-lg overflow-hidden"
                  style={{ background: tableBg, border: `1px solid ${cardBorder}` }}
                >
                  <div
                    className="flex items-center px-5 py-3"
                    style={{ borderBottom: `1px solid ${tableBorderColor}` }}
                  >
                    <h3 className="text-xs font-semibold flex items-center" style={{ color: titleColor }}>
                      <Table2 size={14} style={{ color: accentBlue, marginRight: 8 }} />
                      {t('adminSubscriptions.subscriptionsSummary') || 'Subscriptions Summary'}
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr style={{ background: tableHeaderBg, borderBottom: `1px solid ${tableBorderColor}` }}>
                          <th className="px-5 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Plan</th>
                          <th className="px-5 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Tier</th>
                          <th className="px-5 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Monthly Price</th>
                          <th className="px-5 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Annual Price</th>
                          <th className="px-5 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Active</th>
                          <th className="px-5 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Monthly</th>
                          <th className="px-5 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Annual</th>
                          <th className="px-5 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.subscriptions.map((sub, idx) => (
                          <tr
                            key={sub._id}
                            style={{
                              borderBottom: idx < summary.subscriptions.length - 1 ? `1px solid ${tableBorderColor}` : 'none',
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = tableRowHoverBg; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                          >
                            <td className="px-5 py-3 text-xs font-medium" style={{ color: titleColor }}>{sub.subscription_name}</td>
                            <td className="px-5 py-3 text-xs" style={{ color: subtitleColor }}>{sub.tier}</td>
                            <td className="px-5 py-3 text-xs" style={{ color: subtitleColor }}>{formatCurrency(sub.price.amount_4weeks)}</td>
                            <td className="px-5 py-3 text-xs" style={{ color: subtitleColor }}>{formatCurrency(sub.price.amount_4weeks_annual)}</td>
                            <td className="px-5 py-3 text-xs font-medium" style={{ color: accentBlue }}>{sub.activeUsers}</td>
                            <td className="px-5 py-3 text-xs" style={{ color: subtitleColor }}>{sub.monthlyUsers}</td>
                            <td className="px-5 py-3 text-xs" style={{ color: subtitleColor }}>{sub.annualUsers}</td>
                            <td className="px-5 py-3">
                              <button
                                onClick={() => handleViewUsers(sub.subscription_name)}
                                className="text-xs font-medium"
                                style={{ color: accentBlue, background: 'transparent', border: 'none', cursor: 'pointer' }}
                              >
                                <Eye size={12} className="inline mr-1" />View Users
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <div
                className="rounded-lg overflow-hidden"
                style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
              >
                <div
                  className="flex items-center px-5 py-3"
                  style={{ borderBottom: `1px solid ${tableBorderColor}` }}
                >
                  <h3 className="text-xs font-semibold flex items-center" style={{ color: titleColor }}>
                    <Box size={14} style={{ color: accentBlue, marginRight: 8 }} />
                    {t('adminSubscriptions.allSubscriptions') || 'All Subscriptions'}
                  </h3>
                </div>
                <div className="p-5 space-y-4">
                  {subscriptions.map(sub => (
                    <div
                      key={sub._id}
                      className="rounded-lg p-4 transition-colors"
                      style={{ border: `1px solid ${cardBorder}`, background: mainBg }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = accentBlue; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = cardBorder; }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-sm font-semibold" style={{ color: titleColor }}>{sub.subscription_name}</h4>
                            <span
                              className="px-2 py-0.5 text-xs font-medium rounded"
                              style={{
                                background: sub.is_active ? badgeGreenBg : badgeGrayBg,
                                color: sub.is_active ? badgeGreenText : badgeGrayText,
                              }}
                            >
                              {sub.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span
                              className="px-2 py-0.5 text-xs font-medium rounded"
                              style={{ background: badgeBlueBg, color: badgeBlueText }}
                            >
                              Tier {sub.tier}
                            </span>
                          </div>
                          <p className="text-xs mb-2" style={{ color: subtitleColor }}>{typeof sub.description === 'object' ? (sub.description[lang] || sub.description.en) : sub.description}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                            <div>
                              <p className="text-xs" style={{ color: subtitleColor }}>Monthly Price</p>
                              <p className="text-sm font-medium" style={{ color: titleColor }}>{formatCurrency(sub.price.amount_4weeks)}</p>
                            </div>
                            <div>
                              <p className="text-xs" style={{ color: subtitleColor }}>Annual Price</p>
                              <p className="text-sm font-medium" style={{ color: titleColor }}>{formatCurrency(sub.price.amount_4weeks_annual)}</p>
                            </div>
                            <div>
                              <p className="text-xs" style={{ color: subtitleColor }}>Accounts</p>
                              <p className="text-sm font-medium" style={{ color: titleColor }}>{sub.number_of_accounts}</p>
                            </div>
                            <div>
                              <p className="text-xs" style={{ color: subtitleColor }}>Active Users</p>
                              <p className="text-sm font-medium" style={{ color: accentBlue }}>{sub.stats?.activeUsers || 0}</p>
                            </div>
                          </div>
                          {sub.stripe_price_ids && (
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${tableBorderColor}` }}>
                              <p className="text-xs mb-1" style={{ color: subtitleColor }}>Stripe Price IDs</p>
                              <div className="flex gap-4">
                                <code className="text-xs px-2 py-1 rounded" style={{ background: codeBg, color: codeText }}>
                                  Monthly: {sub.stripe_price_ids.monthly || 'N/A'}
                                </code>
                                <code className="text-xs px-2 py-1 rounded" style={{ background: codeBg, color: codeText }}>
                                  Annual: {sub.stripe_price_ids.annual || 'N/A'}
                                </code>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEditSubscription(sub._id)}
                            className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                            style={{
                              color: subtitleColor,
                              background: buttonBg,
                              border: `1px solid ${buttonBorder}`,
                            }}
                          >
                            <Pencil size={12} className="inline mr-1" />Edit
                          </button>
                          <button
                            onClick={() => handleOpenActionsModal(sub)}
                            className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                            style={{
                              color: accentPurple,
                              background: badgePurpleBg,
                              border: `1px solid ${isDark ? '#7C3AED' : '#DDD6FE'}`,
                            }}
                          >
                            <Zap size={12} className="inline mr-1" />
                            Actions
                            {subscriptionActionsMap[sub._id] !== undefined && (
                              <span
                                className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full"
                                style={{
                                  background: isDark ? '#5B21B6' : '#E9D5FF',
                                  color: isDark ? '#E9D5FF' : '#6B21A8',
                                }}
                              >
                                {subscriptionActionsMap[sub._id]}
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => handleViewUsers(sub.subscription_name)}
                            className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                            style={{
                              color: accentBlue,
                              background: badgeBlueBg,
                              border: `1px solid ${isDark ? '#2563EB' : '#BFDBFE'}`,
                            }}
                          >
                            <Users size={12} className="inline mr-1" />Users
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Annual Users Tab */}
            {activeTab === 'annual' && (
              <div className="space-y-5">
                {/* Stats */}
                {annualStats && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div
                      className="rounded-lg"
                      style={{ background: cardBg, border: `1px solid ${cardBorder}`, padding: 20 }}
                    >
                      <p className="text-xs" style={{ color: subtitleColor }}>Total Annual Users</p>
                      <p className="text-2xl font-bold mt-1" style={{ color: accentPurple }}>{annualStats.totalAnnualUsers}</p>
                    </div>
                    {annualStats.bySubscription?.map((stat: any) => (
                      <div
                        key={stat.subscriptionName}
                        className="rounded-lg"
                        style={{ background: cardBg, border: `1px solid ${cardBorder}`, padding: 20 }}
                      >
                        <p className="text-xs" style={{ color: subtitleColor }}>{stat.subscriptionName}</p>
                        <p className="text-lg font-bold mt-1" style={{ color: titleColor }}>{stat.count} users</p>
                        <p className="text-xs" style={{ color: accentGreen }}>{formatCurrency(stat.totalRevenue)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Users Table */}
                {renderUsersTable(annualUsers, annualPagination, fetchAnnualUsers)}
              </div>
            )}

            {/* Pending Payments Tab */}
            {activeTab === 'pending' && (
              <div className="space-y-5">
                {/* Stats */}
                {pendingStats && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div
                      className="rounded-lg"
                      style={{
                        background: cardBg,
                        border: `1px solid ${isDark ? '#92400E' : '#FED7AA'}`,
                        padding: 20,
                      }}
                    >
                      <p className="text-xs" style={{ color: subtitleColor }}>Total Pending</p>
                      <p className="text-2xl font-bold mt-1" style={{ color: accentOrange }}>{pendingStats.totalPendingPayments}</p>
                    </div>
                    {pendingStats.bySubscription?.map((stat: any) => (
                      <div
                        key={stat.subscriptionName}
                        className="rounded-lg"
                        style={{ background: cardBg, border: `1px solid ${cardBorder}`, padding: 20 }}
                      >
                        <p className="text-xs" style={{ color: subtitleColor }}>{stat.subscriptionName}</p>
                        <p className="text-lg font-bold mt-1" style={{ color: titleColor }}>{stat.count} pending</p>
                        <p className="text-xs" style={{ color: accentOrange }}>{formatCurrency(stat.totalPending)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Users Table */}
                {renderUsersTable(pendingUsers, pendingPagination, fetchPendingUsers, true)}
              </div>
            )}

            {/* Expired Tab */}
            {activeTab === 'expired' && (
              <div className="space-y-5">
                {/* Filter */}
                <div
                  className="rounded-lg p-4"
                  style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                >
                  <div className="flex items-center gap-4">
                    <label className="text-xs font-medium" style={{ color: titleColor }}>Show expired in last:</label>
                    <select
                      value={expiredDaysAgo}
                      onChange={(e) => {
                        setExpiredDaysAgo(Number(e.target.value));
                        fetchExpiredUsers(1);
                      }}
                      className="px-3 py-1.5 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    >
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                    </select>
                  </div>
                </div>

                {/* Users Table */}
                {renderUsersTable(expiredUsers, expiredPagination, fetchExpiredUsers, false, true)}
              </div>
            )}
          </div>
        </div>

        {/* Subscription Users Modal */}
        {selectedSubscription && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{ background: modalBg, backdropFilter: 'blur(10px)' }}
          >
            <div
              className="rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              style={{ background: mainBg, boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}
            >
              <div
                className="px-6 py-4 flex items-center justify-between"
                style={{ borderBottom: `1px solid ${headerBorder}` }}
              >
                <div>
                  <h3 className="text-base font-semibold" style={{ color: titleColor }}>
                    {selectedSubscription} Users
                  </h3>
                  <p className="text-xs" style={{ color: subtitleColor }}>Total: {usersPagination.total} users</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedSubscription(null);
                    setSelectedUserIds([]);
                  }}
                  style={{ color: subtitleColor, background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* Filters */}
              <div
                className="px-6 py-3 flex items-center gap-4"
                style={{ borderBottom: `1px solid ${headerBorder}` }}
              >
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium" style={{ color: titleColor }}>Billing:</label>
                  <select
                    value={billingCycleFilter}
                    onChange={(e) => {
                      setBillingCycleFilter(e.target.value as any);
                      fetchSubscriptionUsers(selectedSubscription, 1);
                    }}
                    className="px-3 py-1.5 text-xs rounded-md"
                    style={{
                      background: inputBg,
                      border: `1px solid ${inputBorder}`,
                      color: inputText,
                    }}
                  >
                    <option value="all">All</option>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>

                {selectedUserIds.length > 0 && (
                  <button
                    onClick={() => setBulkActionModal(true)}
                    className="ml-auto px-3 py-1.5 text-xs font-medium rounded-md"
                    style={{ background: accentBlue, color: '#FFFFFF' }}
                  >
                    <Settings size={12} className="inline mr-1" />
                    Bulk Actions ({selectedUserIds.length})
                  </button>
                )}
              </div>

              <div
                className={`overflow-y-auto max-h-[60vh] ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
              >
                {usersLoading ? (
                  <div className="p-8 text-center">
                    <Spinner size="medium" />
                  </div>
                ) : (
                  renderUsersTable(subscriptionUsers, usersPagination, (page) => fetchSubscriptionUsers(selectedSubscription, page))
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Edit Subscription Modal */}
        {editModal.open && editModal.subscription && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{ background: modalBg, backdropFilter: 'blur(10px)' }}
          >
            <div
              className={`rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
              style={{ background: mainBg, boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}
            >
              <div
                className="px-6 py-4"
                style={{ borderBottom: `1px solid ${headerBorder}` }}
              >
                <h3 className="text-base font-semibold" style={{ color: titleColor }}>
                  Edit {editModal.subscription.subscription_name}
                </h3>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Description (EN)</label>
                    <textarea
                      value={typeof editForm.description === 'object' ? editForm.description.en : (editForm.description || '')}
                      onChange={(e) => {
                        const current = typeof editForm.description === 'object' ? editForm.description : { en: editForm.description || '', fr: '' };
                        setEditForm({ ...editForm, description: { ...current, en: e.target.value } });
                      }}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Description (FR)</label>
                    <textarea
                      value={typeof editForm.description === 'object' ? editForm.description.fr : (editForm.description || '')}
                      onChange={(e) => {
                        const current = typeof editForm.description === 'object' ? editForm.description : { en: editForm.description || '', fr: '' };
                        setEditForm({ ...editForm, description: { ...current, fr: e.target.value } });
                      }}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                      rows={3}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Tagline (EN)</label>
                    <input
                      type="text"
                      value={typeof editForm.tagline === 'object' ? editForm.tagline.en : (editForm.tagline || '')}
                      onChange={(e) => {
                        const current = typeof editForm.tagline === 'object' ? editForm.tagline : { en: editForm.tagline || '', fr: '' };
                        setEditForm({ ...editForm, tagline: { ...current, en: e.target.value } });
                      }}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Tagline (FR)</label>
                    <input
                      type="text"
                      value={typeof editForm.tagline === 'object' ? editForm.tagline.fr : (editForm.tagline || '')}
                      onChange={(e) => {
                        const current = typeof editForm.tagline === 'object' ? editForm.tagline : { en: editForm.tagline || '', fr: '' };
                        setEditForm({ ...editForm, tagline: { ...current, fr: e.target.value } });
                      }}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Amount Monthly</label>
                    <input
                      type="number"
                      value={editForm.price?.amount_monthly || ''}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        price: { ...editForm.price, amount_monthly: Number(e.target.value) }
                      })}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Amount Annually</label>
                    <input
                      type="number"
                      value={editForm.price?.amount_annually || ''}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        price: { ...editForm.price, amount_annually: Number(e.target.value) }
                      })}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Discount %</label>
                    <input
                      type="number"
                      value={editForm.price?.discount_percentage || ''}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        price: { ...editForm.price, discount_percentage: Number(e.target.value) }
                      })}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Price / 4 Weeks (Monthly)</label>
                    <input
                      type="number"
                      value={editForm.price?.amount_4weeks || ''}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        price: { ...editForm.price, amount_4weeks: Number(e.target.value) }
                      })}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Price / 4 Weeks (Annual)</label>
                    <input
                      type="number"
                      value={editForm.price?.amount_4weeks_annual || ''}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        price: { ...editForm.price, amount_4weeks_annual: Number(e.target.value) }
                      })}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Number of Accounts</label>
                  <input
                    type="number"
                    value={editForm.number_of_accounts || ''}
                    onChange={(e) => setEditForm({ ...editForm, number_of_accounts: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{
                      background: inputBg,
                      border: `1px solid ${inputBorder}`,
                      color: inputText,
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Stripe Monthly Price ID</label>
                    <input
                      type="text"
                      value={editForm.stripe_price_ids?.monthly || ''}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        stripe_price_ids: { ...editForm.stripe_price_ids, monthly: e.target.value }
                      })}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                      placeholder="price_xxx"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Stripe Annual Price ID</label>
                    <input
                      type="text"
                      value={editForm.stripe_price_ids?.annual || ''}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        stripe_price_ids: { ...editForm.stripe_price_ids, annual: e.target.value }
                      })}
                      className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                      placeholder="price_xxx"
                    />
                  </div>
                </div>
              </div>

              <div
                className="px-6 py-4 flex gap-3"
                style={{ borderTop: `1px solid ${headerBorder}` }}
              >
                <button
                  onClick={() => setEditModal({ open: false, subscription: null })}
                  disabled={saving}
                  className="flex-1 px-4 py-2 text-xs font-medium rounded-md"
                  style={{
                    color: subtitleColor,
                    background: buttonBg,
                    border: `1px solid ${buttonBorder}`,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSubscription}
                  disabled={saving}
                  className="flex-1 px-4 py-2 text-xs font-medium rounded-md disabled:opacity-50"
                  style={{ background: accentBlue, color: '#FFFFFF' }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Bulk Action Modal */}
        {bulkActionModal && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{ background: modalBg, backdropFilter: 'blur(10px)' }}
          >
            <div
              className="rounded-xl max-w-md w-full"
              style={{ background: mainBg, boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}
            >
              <div
                className="px-6 py-4"
                style={{ borderBottom: `1px solid ${headerBorder}` }}
              >
                <h3 className="text-base font-semibold" style={{ color: titleColor }}>Bulk Action</h3>
                <p className="text-xs" style={{ color: subtitleColor }}>{selectedUserIds.length} users selected</p>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-2" style={{ color: titleColor }}>Action</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="bulkAction"
                        value="extend"
                        checked={bulkAction === 'extend'}
                        onChange={() => setBulkAction('extend')}
                        className="text-blue-600"
                      />
                      <span className="text-xs" style={{ color: titleColor }}>Extend Subscription</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="bulkAction"
                        value="suspend"
                        checked={bulkAction === 'suspend'}
                        onChange={() => setBulkAction('suspend')}
                        className="text-blue-600"
                      />
                      <span className="text-xs" style={{ color: titleColor }}>Suspend Subscription</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="bulkAction"
                        value="cancel"
                        checked={bulkAction === 'cancel'}
                        onChange={() => setBulkAction('cancel')}
                        className="text-blue-600"
                      />
                      <span className="text-xs" style={{ color: dangerText }}>Cancel Subscription</span>
                    </label>
                  </div>
                </div>

                {bulkAction === 'extend' && (
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>Days to Extend</label>
                    <input
                      type="number"
                      value={extendDays}
                      onChange={(e) => setExtendDays(Number(e.target.value))}
                      className="w-full px-3 py-2 text-xs rounded-md"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: inputText,
                      }}
                      min={1}
                    />
                  </div>
                )}

                {bulkAction === 'cancel' && (
                  <div
                    className="p-3 rounded"
                    style={{
                      background: dangerBg,
                      borderLeft: `4px solid ${dangerText}`,
                    }}
                  >
                    <p className="text-xs" style={{ color: dangerText }}>
                      <AlertTriangle size={12} className="inline mr-1" />
                      This action cannot be undone. Users will lose access immediately.
                    </p>
                  </div>
                )}
              </div>

              <div
                className="px-6 py-4 flex gap-3"
                style={{ borderTop: `1px solid ${headerBorder}` }}
              >
                <button
                  onClick={() => setBulkActionModal(false)}
                  className="flex-1 px-4 py-2 text-xs font-medium rounded-md"
                  style={{
                    color: subtitleColor,
                    background: buttonBg,
                    border: `1px solid ${buttonBorder}`,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpdate}
                  className="flex-1 px-4 py-2 text-xs font-medium rounded-md"
                  style={{
                    background: bulkAction === 'cancel' ? (isDark ? '#DC2626' : '#DC2626') : accentBlue,
                    color: '#FFFFFF',
                  }}
                >
                  Apply to {selectedUserIds.length} Users
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Actions Management Modal */}
        {actionsModal.open && actionsModal.subscription && ReactDOM.createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{ background: modalBg, backdropFilter: 'blur(10px)' }}
          >
            <div
              className="rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              style={{ background: mainBg, boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}
            >
              <div
                className="px-6 py-4 flex items-center justify-between"
                style={{ borderBottom: `1px solid ${headerBorder}` }}
              >
                <div>
                  <h3 className="text-base font-semibold flex items-center gap-2" style={{ color: titleColor }}>
                    <Zap size={16} style={{ color: accentPurple }} />
                    {t('adminSubscriptions.actions.title') || 'Manage Actions'}
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: subtitleColor }}>
                    {actionsModal.subscription.subscription_name}
                  </p>
                </div>
                <button
                  onClick={handleCloseActionsModal}
                  style={{ color: subtitleColor, background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <X size={18} />
                </button>
              </div>

              <div
                className={`px-6 py-4 max-h-[60vh] overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
              >
                {actionsLoading ? (
                  <div className="py-8 text-center">
                    <Spinner size="medium" />
                  </div>
                ) : (
                  <>
                    {/* Add Action Section */}
                    {!showAddAction ? (
                      <button
                        onClick={() => setShowAddAction(true)}
                        disabled={getAvailableActionsForAdd().length === 0}
                        className="w-full mb-4 px-4 py-3 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          color: accentPurple,
                          background: badgePurpleBg,
                          border: `1px dashed ${isDark ? '#7C3AED' : '#DDD6FE'}`,
                        }}
                      >
                        <Plus size={12} className="inline mr-2" />
                        {t('adminSubscriptions.actions.addAction') || 'Add New Action'}
                      </button>
                    ) : (
                      <div
                        className="mb-4 p-4 rounded-lg"
                        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-xs font-semibold" style={{ color: titleColor }}>
                            {t('adminSubscriptions.actions.addNewAction') || 'Add New Action'}
                          </h4>
                          <button
                            onClick={() => {
                              setShowAddAction(false);
                              setAddActionForm({ actionId: '', limit: 100, limitUnit: 'day' });
                            }}
                            style={{ color: subtitleColor, background: 'transparent', border: 'none', cursor: 'pointer' }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>
                              {t('adminSubscriptions.actions.selectAction') || 'Select Action'}
                            </label>
                            <select
                              value={addActionForm.actionId}
                              onChange={(e) => setAddActionForm({ ...addActionForm, actionId: e.target.value })}
                              className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              style={{
                                background: inputBg,
                                border: `1px solid ${inputBorder}`,
                                color: inputText,
                              }}
                            >
                              <option value="">{t('adminSubscriptions.actions.choose') || 'Choose an action...'}</option>
                              {getAvailableActionsForAdd().map(action => (
                                <option key={action._id} value={action._id}>
                                  {action.friendly_name || action.name} {action.category ? `(${action.category})` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>
                              {t('adminSubscriptions.actions.limit') || 'Limit'}
                            </label>
                            <input
                              type="number"
                              value={addActionForm.limit}
                              onChange={(e) => setAddActionForm({ ...addActionForm, limit: Number(e.target.value) })}
                              className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              style={{
                                background: inputBg,
                                border: `1px solid ${inputBorder}`,
                                color: inputText,
                              }}
                              min={0}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: titleColor }}>
                              Unit
                            </label>
                            <select
                              value={addActionForm.limitUnit}
                              onChange={(e) => setAddActionForm({ ...addActionForm, limitUnit: e.target.value as 'day' | 'week' })}
                              className="w-full px-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                              style={{
                                background: inputBg,
                                border: `1px solid ${inputBorder}`,
                                color: inputText,
                              }}
                            >
                              <option value="day">Per Day</option>
                              <option value="week">Per Week</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => {
                              setShowAddAction(false);
                              setAddActionForm({ actionId: '', limit: 100, limitUnit: 'day' });
                            }}
                            className="px-3 py-1.5 text-xs font-medium rounded-md"
                            style={{
                              color: subtitleColor,
                              background: buttonBg,
                              border: `1px solid ${buttonBorder}`,
                            }}
                          >
                            {t('common.cancel') || 'Cancel'}
                          </button>
                          <button
                            onClick={handleAddAction}
                            disabled={!addActionForm.actionId || actionSaving}
                            className="px-3 py-1.5 text-xs font-medium rounded-md disabled:opacity-50"
                            style={{ background: accentPurple, color: '#FFFFFF' }}
                          >
                            {actionSaving ? (t('common.saving') || 'Saving...') : (t('adminSubscriptions.actions.add') || 'Add Action')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Actions List */}
                    {subscriptionActions.length === 0 ? (
                      <div className="py-8 text-center">
                        <div
                          className="flex items-center justify-center mx-auto mb-3"
                          style={{
                            width: 48, height: 48, borderRadius: '50%',
                            background: isDark ? '#1D293D' : '#F3F4F6',
                          }}
                        >
                          <Zap size={20} style={{ color: subtitleColor }} />
                        </div>
                        <p className="text-xs" style={{ color: subtitleColor }}>
                          {t('adminSubscriptions.actions.noActions') || 'No actions configured for this subscription'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {subscriptionActions.map((actionItem) => (
                          <div
                            key={actionItem.action_id}
                            className="flex items-center justify-between p-3 rounded-lg transition-colors"
                            style={{
                              background: cardBg,
                              border: `1px solid ${cardBorder}`,
                            }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = isDark ? '#7C3AED' : '#DDD6FE'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = cardBorder; }}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className="flex items-center justify-center rounded-lg"
                                style={{
                                  width: 32, height: 32,
                                  background: badgePurpleBg,
                                }}
                              >
                                <Zap size={14} style={{ color: accentPurple }} />
                              </div>
                              <div>
                                <p className="text-xs font-medium" style={{ color: titleColor }}>
                                  {actionItem.friendly_name || actionItem.name}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {actionItem.category && (
                                    <span
                                      className="text-[10px] px-1.5 py-0.5 rounded"
                                      style={{ background: badgeGrayBg, color: badgeGrayText }}
                                    >
                                      {actionItem.category}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              {editingAction?.actionId === actionItem.action_id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="number"
                                    value={editingAction.limit}
                                    onChange={(e) => setEditingAction({ ...editingAction, limit: Number(e.target.value) })}
                                    className="w-20 px-2 py-1 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    style={{
                                      background: inputBg,
                                      border: `1px solid ${inputBorder}`,
                                      color: inputText,
                                    }}
                                    min={0}
                                  />
                                  <select
                                    value={editingAction.limitUnit}
                                    onChange={(e) => setEditingAction({ ...editingAction, limitUnit: e.target.value as 'day' | 'week' })}
                                    className="w-24 px-2 py-1 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    style={{
                                      background: inputBg,
                                      border: `1px solid ${inputBorder}`,
                                      color: inputText,
                                    }}
                                  >
                                    <option value="day">/ day</option>
                                    <option value="week">/ week</option>
                                  </select>
                                  <button
                                    onClick={handleUpdateActionLimit}
                                    disabled={actionSaving}
                                    className="px-2 py-1 text-xs font-medium rounded disabled:opacity-50"
                                    style={{ background: accentGreen, color: '#FFFFFF' }}
                                  >
                                    <Check size={12} />
                                  </button>
                                  <button
                                    onClick={() => setEditingAction(null)}
                                    className="px-2 py-1 text-xs font-medium rounded"
                                    style={{
                                      color: subtitleColor,
                                      background: isDark ? '#1D293D' : '#F3F4F6',
                                    }}
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="text-right">
                                    <span
                                      className="px-2 py-1 text-xs font-medium rounded"
                                      style={{ background: badgeBlueBg, color: badgeBlueText }}
                                    >
                                      {actionItem.action_limit} / {actionItem.limit_unit === 'week' ? 'week' : 'day'}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => setEditingAction({
                                      actionId: actionItem.action_id,
                                      limit: actionItem.action_limit,
                                      limitUnit: actionItem.limit_unit || 'day'
                                    })}
                                    className="p-1.5 rounded transition-colors"
                                    style={{ color: subtitleColor, background: 'transparent', border: 'none', cursor: 'pointer' }}
                                    title={t('adminSubscriptions.actions.editLimit') || 'Edit limit'}
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveAction(actionItem.action_id)}
                                    disabled={actionSaving}
                                    className="p-1.5 rounded transition-colors disabled:opacity-50"
                                    style={{ color: dangerText, background: 'transparent', border: 'none', cursor: 'pointer' }}
                                    title={t('adminSubscriptions.actions.remove') || 'Remove action'}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div
                className="px-6 py-4"
                style={{ borderTop: `1px solid ${headerBorder}` }}
              >
                <button
                  onClick={handleCloseActionsModal}
                  className="w-full px-4 py-2 text-xs font-medium rounded-md"
                  style={{
                    color: subtitleColor,
                    background: buttonBg,
                    border: `1px solid ${buttonBorder}`,
                  }}
                >
                  {t('common.close') || 'Close'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );

  function renderUsersTable(
    users: SubscriptionUser[],
    pagination: { page: number; total: number; totalPages: number },
    onPageChange: (page: number) => void,
    showAmount: boolean = false,
    showExpired: boolean = false
  ) {
    return (
      <div
        className="rounded-lg overflow-hidden"
        style={{ background: tableBg, border: `1px solid ${cardBorder}` }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: tableHeaderBg, borderBottom: `1px solid ${tableBorderColor}` }}>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={() => selectAllUsers(users)}
                    checked={users.length > 0 && users.every(u => selectedUserIds.includes(u.user_id))}
                    className="rounded"
                    style={{ borderColor: inputBorder }}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>User</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Billing</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Start Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>End Date</th>
                {showAmount && <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Amount</th>}
                {showExpired && <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Days Expired</th>}
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: subtitleColor }}>Last Payment</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td
                    colSpan={showAmount || showExpired ? 9 : 8}
                    className="px-4 py-8 text-center text-xs"
                    style={{ color: subtitleColor }}
                  >
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user, idx) => (
                  <tr
                    key={user._id}
                    style={{
                      borderBottom: idx < users.length - 1 ? `1px solid ${tableBorderColor}` : 'none',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = tableRowHoverBg; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user.user_id)}
                        onChange={() => toggleUserSelection(user.user_id)}
                        className="rounded"
                        style={{ borderColor: inputBorder }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-xs font-medium" style={{ color: titleColor }}>
                          {user.user?.firstName} {user.user?.lastName}
                        </p>
                        <p className="text-xs" style={{ color: subtitleColor }}>{user.user?.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded"
                        style={{
                          background: user.billing_cycle === 'annual' ? badgePurpleBg : badgeBlueBg,
                          color: user.billing_cycle === 'annual' ? badgePurpleText : badgeBlueText,
                        }}
                      >
                        {user.billing_cycle}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: subtitleColor }}>
                      {formatDate(user.subscription_start_date)}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: subtitleColor }}>
                      {formatDate(user.subscription_end_date)}
                    </td>
                    {showAmount && (
                      <td className="px-4 py-3 text-xs font-medium" style={{ color: accentOrange }}>
                        {formatCurrency(user.amount || user.last_payment_amount || 0)}
                      </td>
                    )}
                    {showExpired && (
                      <td className="px-4 py-3 text-xs font-medium" style={{ color: dangerText }}>
                        {user.daysExpired?.toFixed(1) || 'N/A'} days
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded"
                        style={{
                          background: user.status === 'active' || user.status === 'completed'
                            ? badgeGreenBg
                            : user.status === 'pending'
                            ? badgeYellowBg
                            : badgeGrayBg,
                          color: user.status === 'active' || user.status === 'completed'
                            ? badgeGreenText
                            : user.status === 'pending'
                            ? badgeYellowText
                            : badgeGrayText,
                        }}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: subtitleColor }}>
                      {user.last_payment_date ? formatDate(user.last_payment_date) : 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div
            className="px-4 py-3 flex items-center justify-between flex-wrap gap-2"
            style={{ borderTop: `1px solid ${tableBorderColor}` }}
          >
            <p className="text-xs" style={{ color: subtitleColor }}>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1 text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: subtitleColor,
                  border: `1px solid ${buttonBorder}`,
                  background: buttonBg,
                }}
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: subtitleColor,
                  border: `1px solid ${buttonBorder}`,
                  background: buttonBg,
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default SubscriptionManagement;
